import PhoneBoothCloseup from 'assets/hero/phonebooth_closeUp.webp';
import PhoneBoothCloseupMobile from 'assets/hero/phonebooth_closeUp_mobile.webp';
import PhoneBoothMobile from 'assets/hero/phonebooth_mobile.webp';
import PhoneBoothScreenLightOff from 'assets/hero/phonebooth_screen_background_lightOff.webp';
import PhoneBoothScreenLightOn from 'assets/hero/phonebooth_screen_background_lightOn.webp';
import BRXHorizontalLogo from 'assets/logos/brx_logo_horizontal.webp';
import BRXVerticalLogo from 'assets/logos/brx_logo_vertical.webp';
import { useAnalyticsReporter } from 'components/analytics';
import Socials from 'components/Socials/Socials';
import SoundPlayer from 'components/SoundPlayer/SoundPlayer';
import Web3Status from 'components/Web3Status';
import { routes } from 'constants/routes';
import useImagePreloader from 'hooks/useImagePreloader';
import { useScreenSizeRatio } from 'hooks/useScreenSizeRatio';
import React, { useCallback, useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import * as S from './Home.styles';
import HomeFooter from './HomeFooter';
import ScreenHandler from './ScreenHandler';
import StartingOverlay from './StartingOverlay';

const preloadSrcList: string[] = [
  PhoneBoothCloseup,
  PhoneBoothCloseupMobile,
  PhoneBoothMobile,
  PhoneBoothScreenLightOn,
  PhoneBoothScreenLightOff,
];

const Home: React.FC = () => {
  useAnalyticsReporter();

  const [hasExperienceStarted, setHasExperienceStarted] = useState(false);
  const [tans, setTrans] = useState<'phonebooth' | 'closeup' | 'phonescreen'>('phonebooth');
  useImagePreloader(preloadSrcList);

  const [isFadingOut, setIsFadingOut] = useState(false);

  const backgroundRatio = useScreenSizeRatio();
  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    if (
      matchPath(`${routes.phonebooth.home}`, location.pathname) ||
      matchPath(`${routes.phonebooth.status}`, location.pathname)
    ) {
      setTrans('phonescreen');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeup = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => {
      setTrans('phonescreen');
      navigate('/home');
    }, 4000);
  }, [navigate]);

  const fadeOutOne = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => {
      setTrans('closeup');
      setIsFadingOut(false);
      closeup();
    }, 1000);
  }, [closeup]);

  return (
    <S.MainWrapper experienceStarted={hasExperienceStarted}>
      {!hasExperienceStarted && <StartingOverlay onStart={() => setHasExperienceStarted(true)} />}
      <S.HeroWrapper
        experienceStarted={hasExperienceStarted}
        isShowingPhoneScreen={tans === 'closeup' || tans === 'phonescreen'}
        sizeRatio={backgroundRatio}
      >
        {tans === 'phonebooth' && (
          <S.StarterBackgroundWrapper isFadingOut={isFadingOut}>
            <S.LightOnBackground experienceStarted={hasExperienceStarted} />
            <S.LightOffBackground />
            <S.PhoneBoothClicker onClick={() => fadeOutOne()} experienceStarted={hasExperienceStarted}>
              <S.PhoneCallIcon />
            </S.PhoneBoothClicker>
          </S.StarterBackgroundWrapper>
        )}
        {tans === 'closeup' && (
          <S.CloseUpBackground isFadingOut={isFadingOut}>
            <S.CloseupBackground />
          </S.CloseUpBackground>
        )}
        {tans === 'phonescreen' && <ScreenHandler backgroundRatio={backgroundRatio} />}

        <S.ConnectWalletWrapper experienceStarted={hasExperienceStarted && tans !== 'closeup'}>
          <Web3Status />
        </S.ConnectWalletWrapper>

        <S.BRXVerticalLogoWrapper href=".">
          <S.BRXVerticalLogoImg src={BRXVerticalLogo} alt="BRX Logo" />
        </S.BRXVerticalLogoWrapper>
        <S.BRXHorizontalLogoWrapper href="." experienceStarted={hasExperienceStarted && tans !== 'closeup'}>
          <S.BRXHorizontalLogoImg src={BRXHorizontalLogo} alt="BRX Logo" />
        </S.BRXHorizontalLogoWrapper>
        <S.HeroBottomWrapper>
          <S.SocialsWrapper>
            <Socials />
          </S.SocialsWrapper>
          <S.SoundPlayerWrapper experienceStarted={hasExperienceStarted && tans !== 'closeup'}>
            <SoundPlayer startPlaying={hasExperienceStarted} />
          </S.SoundPlayerWrapper>
        </S.HeroBottomWrapper>
        <S.HeroBottomGradient />
      </S.HeroWrapper>
      <S.FooterWrapper experienceStarted={hasExperienceStarted}>
        <HomeFooter />
      </S.FooterWrapper>
    </S.MainWrapper>
  );
};

export default Home;
